/**
 * IllustratedCtaModal — the "artwork on top, centered copy, one CTA" modal
 * used by the post-onboarding welcome (Figma MVP-DESIGNES 9370:2828) and the
 * walkthrough completion (9496:17932). Both frames share the same anatomy:
 *
 * Layout (top to bottom): full-bleed artwork block, centered title and body
 * copy, a single centered CTA button. Both that button and the X only close
 * the modal — there is nothing else to do from here.
 *
 * The default artwork is the flat grey placeholder block from the design
 * (600 × 322) with a large yellow star that pops out above the top edge of
 * the modal (Figma: 396 × 396 at left 102 / top −58). The star is a decorative
 * SVG (``welcome-star.svg``) rendered as a sibling of the Spark Modal inside
 * the portal dialog and positioned absolutely against it — the dialog shares
 * the modal's top-left corner, so Figma's modal-relative offsets apply as-is.
 * Living outside the modal keeps the modal's own corner clip and its body
 * overflow measurement untouched; only the portal dialog's clip is lifted in
 * ``IllustratedCtaModal.css`` so the part of the star above the modal stays
 * visible.
 *
 * Because the star overhangs the box, the portal is also given extra top
 * padding (navbar + overhang) in the stylesheet so the star + modal composite
 * is what gets centered below the navbar — Spark alone would center the box
 * and let the star tip run into the navbar on laptop heights.
 *
 * Callers pass a `className`; it is added next to the generic classes
 * (`<className>`, `<className>-portal`, `<className>__artwork`,
 * `<className>__star`) so each modal can be targeted in tests and styled
 * further without touching the shared stylesheet.
 */
import React from 'react';

import { Modal, SparkModalPortal } from '@spark';

import WelcomeStar from '@/shared/assets/illustrations/welcome-star.svg?react';

import './IllustratedCtaModal.css';

export const ILLUSTRATED_CTA_MODAL_WIDTH = 600;

const BASE = 'illustrated-cta-modal';

const join = (...names) => names.filter(Boolean).join(' ');

export default function IllustratedCtaModal({
    open = false,
    onClose,
    title,
    body,
    ctaLabel,
    className = '',
    testIdPrefix = className,
    width = ILLUSTRATED_CTA_MODAL_WIDTH,
    /** Node rendered inside the modal body; defaults to the grey placeholder block. */
    artwork,
    /**
     * Node rendered as a sibling of the modal inside the portal dialog, above
     * it in layer order; defaults to the pop-out star. Pass `null` for none.
     */
    overlay,
    /**
     * Decorative full-viewport node layered between the dimmed mask and the
     * dialog (e.g. `<Confetti />`); forwarded to `SparkModalPortal`.
     */
    backdrop,
}) {
    const close = () => onClose?.();
    const testId = (suffix) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);
    const artworkNode =
        artwork === undefined ? (
            <div
                className={join(`${BASE}__artwork`, className && `${className}__artwork`)}
                data-testid={testId('artwork')}
                aria-hidden="true"
            />
        ) : (
            artwork
        );
    const overlayNode =
        overlay === undefined ? (
            <WelcomeStar
                className={join(`${BASE}__star`, className && `${className}__star`)}
                data-testid={testId('star')}
                aria-hidden="true"
                focusable="false"
            />
        ) : (
            overlay
        );
    return (
        <SparkModalPortal
            open={open}
            onClose={close}
            width={width}
            className={join(`${BASE}-portal`, className && `${className}-portal`)}
            backdrop={backdrop}
            destroyOnClose
        >
            <Modal
                open
                className={join(BASE, className)}
                title={title}
                subtitle={body}
                showOverlay={false}
                showScrollBar={false}
                showTertiaryButton={false}
                fixedHeight={false}
                primaryLabel={ctaLabel}
                onPrimaryClick={close}
                onCloseClick={close}
            >
                {artworkNode}
            </Modal>
            {overlayNode}
        </SparkModalPortal>
    );
}
